import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { X } from 'lucide-react';
import { useAuth, type Role } from '../features/auth/AuthContext';
import { PasswordVisibilityToggle } from '../components/ui/Input';

function destinationForRole(role: Role): string {
  if (role === 'ADMIN' || role === 'ASSISTANT') return '/admin';
  if (role === 'SUPERVISOR') return '/supervisor';
  if (role === 'TEACHER') return '/teacher';
  if (role === 'PARENT') return '/parent';
  return '/dashboard';
}

const INSTAGRAM_URL = 'https://www.instagram.com/alitqan25academy?stkn=NzZjcWgwcWd1YmZs';
const WHATSAPP_NUMBER = '201012757416';

const BRAND_FEATURE_ICONS = [
  // Professeurs qualifiés
  <path key="teachers" d="M12 3 1 9l11 6 9-4.91V17h2V9L12 3Zm0 8.18L4.24 7 12 2.82 19.76 7 12 11.18ZM5 13.18v4.09c0 .35.18.68.47.87C6.71 18.94 9.14 20 12 20s5.29-1.06 6.53-1.86c.29-.19.47-.52.47-.87v-4.09l-7 3.82-7-3.82Z" />,
  // Coran / mémorisation
  <path key="quran" d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v15H6.5a2.5 2.5 0 0 0-2.5 2.5V4.5ZM6.5 4A.5.5 0 0 0 6 4.5V17c.163 0 .326.006.5 0H18V4H6.5ZM6 19a.5.5 0 0 0 .5.5H19V21H6.5A2.5 2.5 0 0 1 4 18.5V19h2Z" />,
  // Suivi personnalisé / rapports
  <path key="progress" d="M3 3h2v18H3V3Zm4 10h2v8H7v-8Zm4-6h2v14h-2V7Zm4 3h2v11h-2V10Zm4-7h2v18h-2V3Z" />,
  // Certificats
  <path key="certificate" d="M12 2 3 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-9-4Zm0 2.18 7 3.11V12c0 4.52-3.05 8.69-7 9.93-3.95-1.24-7-5.41-7-9.93V7.29l7-3.11ZM12 6l-4 2v3c0 2.76 1.72 5.3 4 6 2.28-.7 4-3.24 4-6V8l-4-2Zm0 2.24 2 1v1.71c0 1.68-.94 3.24-2 3.86-1.06-.62-2-2.18-2-3.86V9.24l2-1Z" />,
] as const;

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { user, isLoading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPasswordRevealed, setIsPasswordRevealed] = useState(false);

  // Un utilisateur déjà connecté qui atterrit ici (ex: en tapant /login à la main,
  // ou via la route racine "/" qui redirige toujours vers /login) doit repartir
  // directement vers son espace plutôt que de revoir le formulaire.
  if (!isLoading && user) {
    return <Navigate to={destinationForRole(user.role)} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const loggedInUser = await login(email, password);
      navigate(destinationForRole(loggedInUser.role));
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.message === 'EMAIL_NOT_VERIFIED') {
        navigate(`/check-email?email=${encodeURIComponent(email)}`);
        return;
      }
      if (isAxiosError(err) && err.response?.data?.message === 'PASSWORD_NOT_SET') {
        setError(t('auth.login.passwordNotSet'));
        return;
      }
      setError(t('auth.login.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const isRtl = i18n.language === 'ar';

  return (
    <div className="relative flex min-h-screen font-manuscript">
      <Link
        to="/"
        aria-label={t('auth.login.backToHome')}
        className={`absolute top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-manuscript-ink/5 text-manuscript-ink-soft transition-colors hover:bg-manuscript-ink/10 hover:text-manuscript-ink md:top-6 ${
          isRtl ? 'left-4 md:left-6' : 'right-4 md:right-6'
        }`}
      >
        <X className="h-5 w-5" aria-hidden />
      </Link>

      {/* Panneau de marque, visible à partir de md */}
      <div className="relative hidden w-[45%] shrink-0 flex-col justify-between overflow-hidden bg-manuscript-ink p-10 text-paper-50 md:flex lg:w-[40%]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1.5px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--color-seal-500), transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--color-manuscript-gold-400), transparent 70%)' }}
        />

        <div className="relative flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-paper-50/20">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-xl font-semibold tracking-tight">{t('common.appName')}</span>
        </div>

        <div className="relative flex flex-1 flex-col justify-center gap-10 py-10">
          <div>
            <p className="font-display text-3xl font-semibold leading-tight text-paper-50">
              {t('auth.login.brandHeadline')}
            </p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-paper-50/70">
              {t('auth.login.brandSubtext')}
            </p>
          </div>

          <ul className="flex flex-col gap-5">
            {(t('auth.login.brandFeatures', { returnObjects: true }) as { title: string; description: string }[]).map(
              (feature, index) => (
                <li key={feature.title} className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-paper-50/10 ring-1 ring-paper-50/15">
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-manuscript-gold-300" aria-hidden="true">
                      {BRAND_FEATURE_ICONS[index]}
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-paper-50">{feature.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-paper-50/60">{feature.description}</p>
                  </div>
                </li>
              ),
            )}
          </ul>
        </div>

        <p className="relative text-xs text-paper-50/40">
          © {new Date().getFullYear()} {t('common.appName')}
        </p>
      </div>

      {/* Formulaire */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-paper-100 px-4 py-12">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05] md:hidden"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #8c2d1e 1.5px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative w-full max-w-sm">
          <Link to="/login" className="mb-8 flex items-center justify-center gap-2.5 md:hidden">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
              <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
            </span>
            <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
          </Link>

          <div className="rounded-2xl border border-manuscript-line-strong bg-paper-50 p-8 shadow-sm">
            <h1 className="font-display text-2xl font-semibold text-manuscript-ink">{t('auth.login.title')}</h1>
            <p className="mt-1.5 text-sm text-manuscript-ink-soft">{t('auth.login.subtitle')}</p>

            <form onSubmit={handleSubmit} className="mt-7 flex flex-col gap-4">
              <div>
                <label htmlFor="email" className="text-sm font-medium text-manuscript-ink-soft">
                  {t('common.email')}
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-manuscript-line-strong bg-paper-100 px-4 py-2.5 text-sm text-manuscript-ink outline-none transition-colors focus:border-seal-500 focus:ring-2 focus:ring-seal-500/20"
                />
              </div>
              <div>
                <label htmlFor="password" className="text-sm font-medium text-manuscript-ink-soft">
                  {t('auth.login.password')}
                </label>
                <div className="relative mt-1.5">
                  <input
                    id="password"
                    type={isPasswordRevealed ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-manuscript-line-strong bg-paper-100 px-4 py-2.5 pr-10 text-sm text-manuscript-ink outline-none transition-colors focus:border-seal-500 focus:ring-2 focus:ring-seal-500/20"
                  />
                  <PasswordVisibilityToggle
                    isRevealed={isPasswordRevealed}
                    onToggle={() => setIsPasswordRevealed((prev) => !prev)}
                  />
                </div>
                <Link
                  to="/forgot-password"
                  className="mt-1.5 inline-block text-xs font-medium text-seal-500 hover:text-seal-400"
                >
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>

              {error && (
                <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-600">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-2 flex w-full items-center justify-center rounded-lg bg-seal-500 px-6 py-3 text-sm font-semibold text-paper-50 shadow-sm transition-colors hover:bg-seal-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? t('auth.login.submitting') : t('auth.login.submit')}
              </button>
            </form>
          </div>

          <div className="mt-6 flex flex-col items-center gap-2">
            <p className="text-xs text-manuscript-ink-faint">{t('auth.login.contactHint')}</p>
            <div className="flex items-center gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('landing.footer.instagramAriaLabel')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-manuscript-line/40 text-manuscript-ink-soft transition-colors hover:bg-seal-500/10 hover:text-seal-500"
              >
                <svg viewBox="0 0 24 24" className="h-4.5 w-4.5 fill-current" aria-hidden="true">
                  <path d="M12 2c-2.72 0-3.06.012-4.123.06-1.062.05-1.79.218-2.427.465a4.9 4.9 0 0 0-1.771 1.153A4.9 4.9 0 0 0 2.525 5.45c-.247.637-.415 1.365-.465 2.427C2.012 8.94 2 9.28 2 12s.012 3.06.06 4.123c.05 1.062.218 1.79.465 2.427a4.9 4.9 0 0 0 1.153 1.771 4.9 4.9 0 0 0 1.771 1.153c.637.247 1.365.415 2.427.465C8.94 21.988 9.28 22 12 22s3.06-.012 4.123-.06c1.062-.05 1.79-.218 2.427-.465a4.9 4.9 0 0 0 1.771-1.153 4.9 4.9 0 0 0 1.153-1.771c.247-.637.415-1.365.465-2.427.048-1.063.06-1.403.06-4.123s-.012-3.06-.06-4.123c-.05-1.062-.218-1.79-.465-2.427a4.9 4.9 0 0 0-1.153-1.771A4.9 4.9 0 0 0 18.55 2.525c-.637-.247-1.365-.415-2.427-.465C15.06 2.012 14.72 2 12 2Zm0 1.802c2.67 0 2.987.01 4.042.059.976.045 1.505.207 1.858.344.467.182.8.399 1.15.748.35.35.566.683.748 1.15.137.353.3.882.344 1.858.048 1.055.058 1.372.058 4.042s-.01 2.987-.058 4.042c-.045.976-.207 1.505-.344 1.858a3.1 3.1 0 0 1-.748 1.15c-.35.35-.683.566-1.15.748-.353.137-.882.3-1.858.344-1.055.048-1.372.058-4.042.058s-2.987-.01-4.042-.058c-.976-.045-1.505-.207-1.858-.344a3.1 3.1 0 0 1-1.15-.748 3.1 3.1 0 0 1-.748-1.15c-.137-.353-.3-.882-.344-1.858-.048-1.055-.059-1.372-.059-4.042s.01-2.987.059-4.042c.045-.976.207-1.505.344-1.858.182-.467.399-.8.748-1.15.35-.35.683-.566 1.15-.748.353-.137.882-.3 1.858-.344 1.055-.048 1.372-.059 4.042-.059Zm0 3.064a5.134 5.134 0 1 0 0 10.268 5.134 5.134 0 0 0 0-10.268Zm0 8.468a3.334 3.334 0 1 1 0-6.668 3.334 3.334 0 0 1 0 6.668Zm6.538-8.671a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z" />
                </svg>
              </a>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(t('whatsappButton.message'))}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('whatsappButton.ariaLabel')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-manuscript-line/40 text-manuscript-ink-soft transition-colors hover:bg-[#25D366]/10 hover:text-[#25D366]"
              >
                <svg viewBox="0 0 32 32" className="h-4.5 w-4.5 fill-current" aria-hidden="true">
                  <path d="M16.004 2.667c-7.364 0-13.333 5.97-13.333 13.333 0 2.351.617 4.646 1.789 6.665L2.667 29.333l6.83-1.79a13.27 13.27 0 0 0 6.507 1.706h.006c7.363 0 13.333-5.97 13.333-13.333 0-3.56-1.387-6.907-3.905-9.425a13.243 13.243 0 0 0-9.428-3.907Zm0 24.4h-.005a11.06 11.06 0 0 1-5.636-1.543l-.404-.24-4.053 1.063 1.082-3.951-.263-.406a11.05 11.05 0 0 1-1.694-5.887c0-6.106 4.968-11.07 11.077-11.07 2.958 0 5.738 1.153 7.83 3.246a11.005 11.005 0 0 1 3.242 7.83c-.003 6.106-4.97 11.07-11.076 11.07Zm6.073-8.29c-.333-.167-1.966-.97-2.27-1.08-.305-.111-.527-.167-.75.166-.222.334-.86 1.08-1.054 1.303-.194.223-.389.25-.722.084-.334-.167-1.409-.519-2.684-1.655-.992-.885-1.663-1.978-1.858-2.312-.194-.334-.021-.514.146-.68.15-.15.334-.39.5-.585.167-.195.223-.334.334-.556.111-.223.056-.417-.028-.584-.083-.167-.75-1.806-1.028-2.474-.271-.65-.546-.562-.75-.572-.194-.01-.417-.012-.639-.012a1.23 1.23 0 0 0-.888.417c-.305.334-1.165 1.14-1.165 2.778s1.193 3.222 1.36 3.445c.166.222 2.35 3.59 5.695 5.034.796.343 1.417.548 1.901.702.799.254 1.526.218 2.101.132.641-.096 1.966-.804 2.244-1.581.278-.777.278-1.443.194-1.581-.083-.14-.305-.223-.639-.39Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
